import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiWallet } from "react-icons/bi";
import { toast } from "react-toastify";
import { CustomButton } from "../../../atoms/CustomButton";
import { Switch } from "../../../atoms/Switch";
import useAuth from "../../../../hooks/useAuth";
import { useUploadFile } from "../../../../hooks/cdn";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../modals/PlansModal";
import ConfirmModal from "../../../public/ConfirmModal";
import { HiOutlinePhotograph, HiOutlineUpload, HiX } from "react-icons/hi";
import { Spinner } from "../../../atoms/Spinner";
import parse from "html-react-parser";

interface WalletSettingsData {
  wallet_settings?: {
    wallet_enabled: boolean;
    apple_banner_image?: string;
    google_banner_image?: string;
  };
}

interface WalletSettingsProps {
  data?: WalletSettingsData;
  onSubmit: (formData: WalletSettingsData) => Promise<void>;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "avif"];

const isImageFile = (file: File): boolean => {
  if (file.type && file.type.startsWith("image/")) return true;
  const ext = file.name.split(".").pop()?.toLowerCase();
  return ACCEPTED_IMAGE_EXTENSIONS.includes(ext || "");
};

const GOOGLE_REQUIRED_WIDTH = 1032;
const GOOGLE_REQUIRED_HEIGHT = 336;
const GOOGLE_ASPECT_RATIO = GOOGLE_REQUIRED_WIDTH / GOOGLE_REQUIRED_HEIGHT;

const APPLE_REQUIRED_WIDTH = 640;
const APPLE_REQUIRED_HEIGHT = 246;
const APPLE_ASPECT_RATIO = APPLE_REQUIRED_WIDTH / APPLE_REQUIRED_HEIGHT;

const WalletSettings: React.FC<WalletSettingsProps> = ({ data, onSubmit }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [appleBannerImage, setAppleBannerImage] = useState<string | undefined>(
    undefined
  );
  const [googleBannerImage, setGoogleBannerImage] = useState<
    string | undefined
  >(undefined);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRemoveAppleConfirm, setShowRemoveAppleConfirm] = useState(false);
  const [showRemoveGoogleConfirm, setShowRemoveGoogleConfirm] = useState(false);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const hasAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const canWrite = permissions.can(Cap.LOYALTY, Level.WRITE);

  useEffect(() => {
    if (data?.wallet_settings) {
      setWalletEnabled(data.wallet_settings.wallet_enabled || false);
      setAppleBannerImage(data.wallet_settings.apple_banner_image || undefined);
      setGoogleBannerImage(
        data.wallet_settings.google_banner_image || undefined
      );
      setHasChanges(false);
    }
  }, [data]);

  const handleToggle = async (enabled: boolean) => {
    const savedValue = data?.wallet_settings?.wallet_enabled || false;
    if (enabled === savedValue && !hasChanges) return;

    const prev = walletEnabled;
    setWalletEnabled(enabled);

    try {
      setIsSubmitting(true);
      await onSubmit({
        wallet_settings: {
          wallet_enabled: enabled,
          apple_banner_image: appleBannerImage,
          google_banner_image: googleBannerImage,
        },
      });
      setHasChanges(false);
    } catch {
      setWalletEnabled(prev);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    walletType: "apple" | "google"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      toast.error(
        t("dashboard.features.lmc.walletSettings.invalidFileType") as string
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        t("dashboard.features.lmc.walletSettings.fileTooLarge") as string
      );
      return;
    }

    const requiredWidth =
      walletType === "google" ? GOOGLE_REQUIRED_WIDTH : APPLE_REQUIRED_WIDTH;
    const requiredHeight =
      walletType === "google" ? GOOGLE_REQUIRED_HEIGHT : APPLE_REQUIRED_HEIGHT;

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      if (img.width !== requiredWidth || img.height !== requiredHeight) {
        toast.error(
          t("dashboard.features.lmc.walletSettings.invalidDimensions", {
            width: requiredWidth,
            height: requiredHeight,
          }) as string
        );
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      uploadFile(
        {
          user_identification: user.user_identification,
          entity_identification: user.entity_identification,
          data: formData,
        },
        {
          onSuccess: (res: any) => {
            const cdnPath = res?.data?.element?.[0]?.cdnPath;
            if (cdnPath) {
              if (walletType === "google") {
                setGoogleBannerImage(cdnPath);
              } else {
                setAppleBannerImage(cdnPath);
              }
              setHasChanges(true);
            }
          },
        }
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      toast.error(
        t("dashboard.features.lmc.walletSettings.invalidFileType") as string
      );
    };
    img.src = URL.createObjectURL(file);

    if (e.target) e.target.value = "";
  };

  const handleRemoveBanner = (walletType: "apple" | "google") => {
    if (walletType === "google") {
      setShowRemoveGoogleConfirm(false);
      setGoogleBannerImage(undefined);
    } else {
      setShowRemoveAppleConfirm(false);
      setAppleBannerImage(undefined);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit({
        wallet_settings: {
          wallet_enabled: walletEnabled,
          apple_banner_image: appleBannerImage,
          google_banner_image: googleBannerImage,
        },
      });
      setHasChanges(false);
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="w-full mt-6">
        <div className="flex items-center justify-center gap-3 mb-4">
          <img
            src="https://cdn.1cdn.app/application/UMD/26022610561174_US-UK_Add_to_Apple_Wallet_RGB_101421.svg"
            alt="Apple Wallet"
            className="h-10"
          />
          <img
            src="https://cdn.1cdn.app/application/UMD/26022610561140_enUS_add_to_google_wallet_add-wallet-badge.svg"
            alt="Google Wallet"
            className="h-10"
          />
        </div>

        <div className="flex items-center mb-4 gap-2">
          <BiWallet className="text-gray-400 flex-shrink-0" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.walletSettings.title")}
          </h3>
        </div>

        <FeatureUpgradePrompt
          featureName={
            t("dashboard.features.lmc.walletSettings.infoBoxTitle") as string
          }
          featureDescription={
            t("dashboard.features.lmc.walletSettings.description") as string
          }
          benefits={[
            t("dashboard.features.lmc.walletSettings.benefit1") as string,
            t("dashboard.features.lmc.walletSettings.benefit2") as string,
            t("dashboard.features.lmc.walletSettings.benefit3") as string,
          ]}
          onUpgrade={() => setShowUpgradeModal(true)}
          className="mb-5"
        />

        <PlansModal
          showPlansModal={showUpgradeModal}
          setShowPlansModal={setShowUpgradeModal}
          targetPlan={PlanTier.PREMIUM}
        />

        <div className="self-stretch h-px bg-gray-200 block mt-6" />
      </div>
    );
  }

  return (
    <div className="w-full mt-6">
      <div className="flex items-center justify-center gap-3 mb-4">
        <img
          src="https://cdn.1cdn.app/application/UMD/26022610561174_US-UK_Add_to_Apple_Wallet_RGB_101421.svg"
          alt="Apple Wallet"
          className="h-10"
        />
        <img
          src="https://cdn.1cdn.app/application/UMD/26022610561140_enUS_add_to_google_wallet_add-wallet-badge.svg"
          alt="Google Wallet"
          className="h-10"
        />
      </div>

      <div className="flex items-center mb-4 gap-2">
        <BiWallet className="text-gray-400 flex-shrink-0" size={18} />
        <h3 className="text-base font-semibold text-gray-900">
          {t("dashboard.features.lmc.walletSettings.title")}
        </h3>
      </div>

      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white border border-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
              <BiWallet size={18} className="text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">
                {t("dashboard.features.lmc.walletSettings.infoBoxTitle")}
              </h4>
              <p className="mt-0.5 text-xs text-gray-500">
                {t("dashboard.features.lmc.walletSettings.infoBoxDescription")}
              </p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-md p-3 text-xs">
            <p className="mb-2 font-semibold text-gray-900">
              {t("dashboard.features.lmc.walletSettings.benefitsTitle")}
            </p>
            <ol className="flex flex-col gap-1.5 text-gray-700">
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">1.</span>
                <span>
                  {t("dashboard.features.lmc.walletSettings.benefit1")}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">2.</span>
                <span>
                  {t("dashboard.features.lmc.walletSettings.benefit2")}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="tabular-nums text-gray-400">3.</span>
                <span>
                  {t("dashboard.features.lmc.walletSettings.benefit3")}
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div>
            <label
              htmlFor="review_link_enabled"
              className="text-gray-700 text-sm font-semibold font-figtree"
            >
              {t("dashboard.features.lmc.walletSettings.enableToggleLabel")}
            </label>
            <p className="text-gray-500 text-xs font-normal font-figtree mt-1">
              {t(
                "dashboard.features.lmc.walletSettings.enableToggleDescription"
              )}
            </p>
          </div>
          <Switch
            id="wallet_enabled"
            checked={walletEnabled}
            disabled={isSubmitting || !canWrite}
            onChange={handleToggle}
          />
        </div>
      </div>

      {walletEnabled && (
        <div className="mb-4 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t("dashboard.features.lmc.walletSettings.googleBannerLabel")}
              </label>
            </div>
            <p className="text-gray-500 text-xs font-normal font-figtree mb-3">
              {parse(
                t(
                  "dashboard.features.lmc.walletSettings.googleBannerDescription"
                )
              )}
            </p>

            {googleBannerImage ? (
              <div className="space-y-2">
                <p className="text-gray-600 text-xs font-semibold">
                  {t("dashboard.features.lmc.walletSettings.bannerPreview")}
                </p>
                <div className="relative inline-block">
                  <div
                    className="relative rounded-lg overflow-hidden border-2 border-gray-300"
                    style={{
                      aspectRatio: GOOGLE_ASPECT_RATIO.toString(),
                      maxWidth: "400px",
                    }}
                  >
                    <img
                      src={googleBannerImage}
                      alt="Google Wallet banner"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => setShowRemoveGoogleConfirm(true)}
                    disabled={!canWrite}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    title={
                      t(
                        "dashboard.features.lmc.walletSettings.removeBanner"
                      ) as string
                    }
                  >
                    <HiX size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <HiOutlinePhotograph
                  size={40}
                  className="text-gray-400 mx-auto mb-3"
                />
                <p className="text-sm text-gray-600 mb-3">
                  <span className="text-gray-600 text-xs font-semibold bg-gray-100 px-2 py-1 rounded">
                    {t("dashboard.features.lmc.walletSettings.requirement")}{" "}
                    {GOOGLE_REQUIRED_WIDTH}x{GOOGLE_REQUIRED_HEIGHT} pixels
                  </span>
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, "google")}
                  className="hidden"
                  id="google-banner-input"
                />
                <CustomButton
                  type="button"
                  variant="primary"
                  className="inline-flex"
                  onClick={() =>
                    document.getElementById("google-banner-input")?.click()
                  }
                  disabled={isUploading || !canWrite}
                  icon={
                    isUploading ? (
                      <Spinner size="sm" />
                    ) : (
                      <HiOutlineUpload size={16} />
                    )
                  }
                  title={
                    isUploading
                      ? t("dashboard.features.lmc.walletSettings.uploading")
                      : t("dashboard.features.lmc.walletSettings.uploadBanner")
                  }
                />
                <p className="text-xs text-gray-500 mt-3">
                  {t(
                    "dashboard.features.lmc.walletSettings.bannerImageFormats"
                  )}
                </p>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t("dashboard.features.lmc.walletSettings.appleBannerLabel")}
              </label>
            </div>
            <p className="text-gray-500 text-xs font-normal font-figtree mb-3">
              {parse(
                t(
                  "dashboard.features.lmc.walletSettings.appleBannerDescription"
                )
              )}
            </p>

            {appleBannerImage ? (
              <div className="space-y-2">
                <p className="text-gray-600 text-xs font-semibold">
                  {t("dashboard.features.lmc.walletSettings.bannerPreview")}
                </p>
                <div className="relative inline-block">
                  <div
                    className="relative rounded-lg overflow-hidden border-2 border-gray-300"
                    style={{
                      aspectRatio: APPLE_ASPECT_RATIO.toString(),
                      maxWidth: "400px",
                    }}
                  >
                    <img
                      src={appleBannerImage}
                      alt="Apple Wallet banner"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => setShowRemoveAppleConfirm(true)}
                    disabled={!canWrite}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    title={
                      t(
                        "dashboard.features.lmc.walletSettings.removeBanner"
                      ) as string
                    }
                  >
                    <HiX size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <HiOutlinePhotograph
                  size={40}
                  className="text-gray-400 mx-auto mb-3"
                />
                <p className="text-sm text-gray-600 mb-3">
                  <span className="text-gray-600 text-xs font-semibold bg-gray-100 px-2 py-1 rounded">
                    {t("dashboard.features.lmc.walletSettings.requirement")}{" "}
                    {APPLE_REQUIRED_WIDTH}x{APPLE_REQUIRED_HEIGHT} pixels
                  </span>
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(e, "apple")}
                  className="hidden"
                  id="apple-banner-input"
                />
                <CustomButton
                  type="button"
                  variant="primary"
                  className="inline-flex"
                  onClick={() =>
                    document.getElementById("apple-banner-input")?.click()
                  }
                  disabled={isUploading || !canWrite}
                  icon={
                    isUploading ? (
                      <Spinner size="sm" />
                    ) : (
                      <HiOutlineUpload size={16} />
                    )
                  }
                  title={
                    isUploading
                      ? t("dashboard.features.lmc.walletSettings.uploading")
                      : t("dashboard.features.lmc.walletSettings.uploadBanner")
                  }
                />
                <p className="text-xs text-gray-500 mt-3">
                  {t(
                    "dashboard.features.lmc.walletSettings.bannerImageFormats"
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {hasChanges && (
        <div className="flex justify-end mt-4">
          <CustomButton
            type="button"
            onClick={handleSave}
            disabled={isSubmitting || !canWrite}
            variant="primary"
            className=""
            title={
              isSubmitting ? "Saving..." : (t("common.saveShort") as string)
            }
          />
        </div>
      )}

      <ConfirmModal
        show={showRemoveGoogleConfirm}
        onClose={setShowRemoveGoogleConfirm}
        title={
          t(
            "dashboard.features.lmc.walletSettings.removeGoogleBannerConfirmTitle"
          ) as string
        }
        description={
          t(
            "dashboard.features.lmc.walletSettings.removeGoogleBannerConfirmDescription"
          ) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={setShowRemoveGoogleConfirm}
        confirmBtnName={t("common.remove") as string}
        confirmBtnFn={() => handleRemoveBanner("google")}
        ConfirmBtnColor="red"
      />

      <ConfirmModal
        show={showRemoveAppleConfirm}
        onClose={setShowRemoveAppleConfirm}
        title={
          t(
            "dashboard.features.lmc.walletSettings.removeAppleBannerConfirmTitle"
          ) as string
        }
        description={
          t(
            "dashboard.features.lmc.walletSettings.removeAppleBannerConfirmDescription"
          ) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={setShowRemoveAppleConfirm}
        confirmBtnName={t("common.remove") as string}
        confirmBtnFn={() => handleRemoveBanner("apple")}
        ConfirmBtnColor="red"
      />

      <div className="self-stretch h-px bg-gray-200 block mt-6" />
    </div>
  );
};

export default React.memo(WalletSettings);
